import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import confetti from "canvas-confetti";
import { Row, RowState } from "./Row";
import { Clue, clue, CluedLetter, describeClue } from "./clue";
import { Keyboard } from "./Keyboard";
import { PopUp } from "./PopUp"
import { getTakeoverDismissKey, maybeFetchTakeover } from "./takeover";
import TakeoverNotice from "./TakeoverNotice";
import type { Takeover } from "./takeover";
import {
  gameName,
  pick,
  speak,
  practice,
  dayNum,
  todayDayNum,
  cheat,
  makeRandom,
  urlParam,
  isDev,
  needResetPractice,
  currentSeed,
  day1Date,
  research
} from "./util";
import { getUserId } from "./userId";
import { notePlayed } from "./activePing";
import { saveGameState, findWatchableGames, WatchableGames, serializeClues } from "./gameStateApi";
import { WatchView } from "./WatchView";

import { hardCodedPuzzles } from "./hardcoded";
import { hardCodedPractice } from "./hardcoded_practice";
import { Day } from "./Stats"
import { readOnly, serializeStorage } from "./App";

import cheatyface from "./cheatyface.json";

import real_full_dictionary from "./dictionary.json";
import real_full_targets from "./targets.json";

const minWordLength = 4;
const maxWordLength = 7;

const full_dictionary = real_full_dictionary.filter(
  (word: string) => word.length >= minWordLength && word.length <= maxWordLength);

const full_targets = real_full_targets.filter(
  (word: string) => word.length >= minWordLength && word.length <= maxWordLength);

export enum GameState {
  Playing,
  Won,
  Lost,
}

declare const checkVersion: Function;

export const gameDayStoragePrefix = "DD1-result-";
export const gameDayResetsPrefix = "DD1-resets-";
export const hardModeStoragePrefix = "DD1-hard-mode-";
export const guessesDayStoragePrefix = "DD1-guesses-";

function useLocalStorage<T>(
  key: string,
  initial: T
): [T, (value: T | ((t: T) => T)) => void] {
  const [current, setCurrent] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initial;
    } catch (e) {
      return initial;
    }
  });
  const setSetting = (value: T | ((t: T) => T)) => {
    try {
      const v = value instanceof Function ? value(current) : value;
      setCurrent(v);
      window.localStorage.setItem(key, JSON.stringify(v));
    } catch (e) {}
  };
  return [current, setSetting];
}

function useLocalStorageWriteImmediately<T>(
  key: string,
  initial: T
): [T, (value: T | ((t: T) => T)) => void] {
  const [current, setCurrent] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (!item) {
        window.localStorage.setItem(key, JSON.stringify(initial));
      }
      return item ? JSON.parse(item) : initial;
    } catch (e) {
      window.localStorage.setItem(key, JSON.stringify(initial));
      return initial;
    }
  });
  const setSetting = (value: T | ((t: T) => T)) => {
    try {
      const v = value instanceof Function ? value(current) : value;
      setCurrent(v);
      window.localStorage.setItem(key, JSON.stringify(v));
    } catch (e) {}
  };
  return [current, setSetting];
}

function gameOverText(state: GameState, target: string) : string {
  const verbed = state === GameState.Won ? "won!" : "lost.";
  return `You ${verbed} The word was ${target.toUpperCase().replaceAll(" ", "⎵")}.`; 
}

export async function copyImportCode() {
  try {
    await navigator.clipboard.writeText(serializeStorage());
    return;
  } catch (e) {
    console.warn("navigator.clipboard.writeText failed:", e);
  }
}

function loadPuzzle(seed: number) : Puzzle|undefined {
  let hardCoded = hardCodedPuzzles[seed];
  if (hardCoded && !practice) {
    if (full_dictionary.includes(hardCoded.target) ) {
      return hardCoded;
    }
    else {
      window.console.log("ERROR: " + hardCoded.target);
    }
  }
  hardCoded = hardCodedPractice[seed];
  if (hardCoded && practice) {
    if (full_dictionary.includes(hardCoded.target) ) {
      return hardCoded;
    }
    else {
      window.console.log("ERROR: " + hardCoded.target);
    }
  }
  return undefined;
}

function randomTarget(random: ()=>number, eligible: string[]): string {
  let candidate = pick(eligible, random);
  while (/\*/.test(candidate)) {
    candidate = pick(eligible, random);
  }
  while(candidate.length < 7) {
    candidate = random() < .5 ? " " + candidate : candidate + " ";
  }
  return candidate;
}

let uniqueGame = practice ? 600000 : 6000;
export function makePuzzle(seed: number) : Puzzle { 
  let loadedPuzzle = loadPuzzle(seed);
  if (loadedPuzzle) {
    return loadedPuzzle;
  }
  let random = makeRandom(seed+uniqueGame);

  const eligible = full_targets
    .slice(0, full_targets.indexOf("murky") + 1);

  let target = randomTarget(random, eligible);
  let puzzle: Puzzle = {
    target: target
  };
  return puzzle;
}

export function cluesEqual(clue1: CluedLetter[], clue2: CluedLetter[]) {
  if (clue1.length != clue2.length ) {
    return false;
  }
  for(var i = 0; i < clue1.length; ++i ) {
    if(clue1[i].clue != clue2[i].clue ) {
      return false;
    }
  }
  return true;
}

export function emojiBlock(day: Day, colorBlind: boolean) : string {
  const emoji = colorBlind
    ? ["⬛", "🟦", "🟧"]
    : ["⬛", "🟨", "🟩"];
  return day.guesses.map((guess) =>
        clue(guess, day.puzzle.target)
          .map((c) => emoji[c.clue ?? 0])
          .join("")
      )
      .join("\n");
}

export interface Puzzle {
  target: string
}

interface GameProps {
  maxGuesses: number;
  hidden: boolean;
  colorBlind: boolean;
  keyboardLayout: string;
  hardMode: boolean;
  liveReplays: boolean;
  setLiveReplays: (value: boolean | ((t: boolean) => boolean)) => void;
  onWatchingChange?: (watching: boolean) => void;
}

function Game(props: GameProps) {

  if (isDev && urlParam("export")) {
    let values : Record<number, Puzzle> = {};    
    for(let i = 1; i <= parseInt(urlParam("export") ?? "1"); ++i) {
      if (practice)
        continue;
      values[i] = makePuzzle(i);
    }
    window.console.log( JSON.stringify(values, null, "\t") );
  }

  const [puzzle, setPuzzle] = useState(() => {
    let p = makePuzzle(currentSeed);
    return p;
  });

  useEffect(() => {
    if(dayNum != 34 || practice)
      return;
    if(guesses.length == 0 && puzzle.target == "silesia") {
      puzzle.target = " sorry ";
      setResetCount((count) => count+1);
    }
    else if(resetCount > 0) {
      puzzle.target = " sorry ";
    }
  });
  
  let hardModeStorageKey = practice ? ("practiceHardMode") : (hardModeStoragePrefix+currentSeed);
  let stateStorageKey = practice ? ("practiceState") : (gameDayStoragePrefix+currentSeed);
  let resetVersionKey = practice ? ("practiceState") : (gameDayResetsPrefix+currentSeed);
  let guessesStorageKey = practice ? ("practiceGuesses") : (guessesDayStoragePrefix+currentSeed);

  const [gameState, setGameState] = useLocalStorage<GameState>(stateStorageKey, GameState.Playing);
  const [resetCount, setResetCount] = useLocalStorage<number>(resetVersionKey, 0);
  const [hardModeState, setHardModeState] = useLocalStorageWriteImmediately<boolean>(hardModeStorageKey, props.hardMode);
  const [guesses, setGuesses] = useLocalStorage<string[]>(guessesStorageKey, new Array(0));
  const [currentGuess, setCurrentGuess] = useState<[string,number]>(["       ",0]);
  const [hint, setHint] = useState<string>(getHintFromState());
  const [keyboardJuice, setKeyboardJuice] = useState(false);
  const juicedAtGuessCount = useRef<number>(-1);

  const canReset = isDev || 
      (dayNum == 34 && resetCount == 0 && puzzle.target == "silesia");

  const resetDay = () => {
    if (canReset) {
      window.localStorage.removeItem(gameDayStoragePrefix+dayNum);
      window.localStorage.removeItem(hardModeStoragePrefix+dayNum);
      window.localStorage.removeItem(guessesDayStoragePrefix+dayNum);
      setResetCount((count) => count+1);
    }
  }

  const resetPractice = () => {
    window.localStorage.removeItem("practice");
    window.localStorage.removeItem("practiceState");
    window.localStorage.removeItem("practiceHardMode");
    window.localStorage.removeItem("practiceGuesses");
  }

  if (needResetPractice) {
    resetPractice();
  }

  // --- Watch feature state ---
  const puzzleId = practice ? "practice-" + currentSeed : String(currentSeed);
  const [watchGames, setWatchGames] = useState<WatchableGames | null>(null);
  const [showWatchIntro, setShowWatchIntro] = useState(false);
  const [watching, setWatching] = useState(false);
  const [watchingKind, setWatchingKind] = useState<"live" | "replay">("live");
  const [watchingUserId, setWatchingUserId] = useState<string | undefined>(undefined);

  useEffect(() => {
    props.onWatchingChange?.(watching);
  }, [watching]);

  // Sync game state to remote API
  const syncToRemote = useCallback(() => {
    if (!props.liveReplays || cheat) return;
    const userId = getUserId();
    const status = gameState === GameState.Won ? "won" as const
      : gameState === GameState.Lost ? "lost" as const
      : "in_progress" as const;
    saveGameState(userId, puzzleId, {
      metadata: {
        status,
        numGuesses: guesses.length,
        puzzleId,
      },
      state: {
        guesses: guesses.map((g) => ({
          word: g,
          clues: serializeClues(clue(g, puzzle.target)),
        })),
        currentGuess: currentGuess[0],
        cursorPosition: currentGuess[1],
      },
    });
  }, [props.liveReplays, gameState, guesses, currentGuess, puzzle.target, puzzleId]);

  // Sync on every state change
  useEffect(() => {
    syncToRemote();
  }, [syncToRemote]);

  // Poll for watchable games after game over
  useEffect(() => {
    if (gameState === GameState.Playing) return;
    const userId = getUserId();
    let active = true;
    const poll = () => {
      findWatchableGames(puzzleId, userId).then((games) => {
        if (active) setWatchGames(games);
      });
    };
    poll();
    const interval = setInterval(poll, 10000);
    return () => { active = false; clearInterval(interval); };
  }, [gameState, puzzleId]);

  function pickGame(kind: "live" | "replay", afterUserId?: string): import("./gameStateApi").RemoteGameState | null {
    const list = kind === "live" ? watchGames?.liveGames : watchGames?.replayGames;
    if (!list || list.length === 0) return null;
    if (!afterUserId) return list[0];
    const currentIdx = list.findIndex((g) => g.user_id === afterUserId);
    const nextIdx = (currentIdx + 1) % list.length;
    return list[nextIdx];
  }

  function startWatchingKind(kind: "live" | "replay") {
    const game = pickGame(kind);
    if (!game) return;
    setWatchingKind(kind);
    setWatchingUserId(game.user_id);
    setWatching(true);
  }

  function switchToKind(kind: "live" | "replay") {
    const afterId = kind === watchingKind ? watchingUserId : undefined;
    const game = pickGame(kind, afterId);
    if (!game) return;
    setWatching(false);
    setTimeout(() => {
      setWatchingKind(kind);
      setWatchingUserId(game.user_id);
      setWatching(true);
    }, 0);
  }

  const handleWatchClick = (kind: "live" | "replay") => {
    const introSeen = window.localStorage.getItem("shuntle-watch-intro-seen");
    if (!props.liveReplays || !introSeen) {
      setShowWatchIntro(true);
    } else {
      startWatchingKind(kind);
    }
  };

  const liveCount = watchGames?.liveGames.length ?? 0;
  const replayCount = watchGames?.replayGames.length ?? 0;

  const tableRef = useRef<HTMLTableElement>(null);
  async function share(copiedHint: string, text?: string) {
    
    if (window.location.protocol === "http:") {
      if (window.confirm(
        "Only secure websites (https, not http) can access the clipboard/sharing."
        + " Would you like to switch to the https site?"
        + " Note that game history is separate between http and https and you may need to update your bookmark."
      )) {
        window.location.protocol = "https:";
        return;
      }
    }

    const url = window.location.origin + window.location.pathname + (practice ? ("?unlimited=" + currentSeed.toString()) : "");
    const body = (text ? text + "\n" : "") + url;
    if (
      /android|iphone|ipad|ipod|webos/i.test(navigator.userAgent) &&
      !/firefox/i.test(navigator.userAgent)
    ) {
      try {
        await navigator.share({ text: body });
        return;
      } catch (e) {
        console.warn("navigator.share failed:", e);
      }
    }
    try {
      await navigator.clipboard.writeText(body);
      setHint(copiedHint);
      return;
    } catch (e) {
      console.warn("navigator.clipboard.writeText failed:", e);
    }
    setHint(url);
  }

  function getHintFromState() {    
    if  (gameState === GameState.Won || gameState === GameState.Lost) {
      return gameOverText(gameState, puzzle.target);
    }
    return "";
  }

  const onKey = (key: string) => {
    if (gameState !== GameState.Playing) {
      return;
    }

    const realMaxGuesses = props.maxGuesses;
  
    if (guesses.length === realMaxGuesses) {
      return;
    }
    if (/^[a-z]$/i.test(key)) {
      setCurrentGuess(([guess,cursor]) => {
        if ( cursor == -1 ) return [guess,cursor];
          return [
            (guess.slice(0,cursor) + key.toLowerCase() 
              + guess.slice(cursor+1)).slice(0, puzzle.target.length), 
              cursor == (puzzle.target.length-1) ? -1 : cursor+1]
        }        
      );
      tableRef.current?.focus();
      setHint(getHintFromState());
    } else if (key === "Backspace") {
      setCurrentGuess(([guess,cursor]) => {
        let realCursor = cursor == -1 ? puzzle.target.length : cursor;
        if (cursor != -1 && guess[cursor] != " " ) {
          // delete at the cursor and back up if the cursor is currently on a typed letter
          return [(guess.slice(0,cursor) + " " + guess.slice(cursor+1)).slice(0,puzzle.target.length),Math.max(0,realCursor-1)];
        }
        // if the cursor is on a space, delete behind the cursor and back up
        return [(guess.slice(0,realCursor-1) + " " + guess.slice(realCursor)).slice(0, puzzle.target.length),Math.max(0,realCursor-1)]
      });
      setHint(getHintFromState());
    } else if (key == "SpaceBar" || key == " ") {
      setCurrentGuess(([guess,cursor]) => {
        if ( cursor == -1 ) return [guess,cursor];
        return [(guess.slice(0,cursor) + " " + guess.slice(cursor+1)).slice(0, puzzle.target.length), 
          cursor == (puzzle.target.length-1) ? -1 : cursor+1]
      });
    } else if (key === "ArrowRight") {
      setCurrentGuess(([guess,cursor]) => [guess,cursor == (puzzle.target.length-1) ? 0 : cursor+1]);
    } else if (key === "ArrowLeft") {
      setCurrentGuess(([guess,cursor]) => [guess,cursor == -1 ? (puzzle.target.length-1) : cursor == 0 ? (puzzle.target.length-1) : cursor-1]);
    } else if (key === "Delete") {
      setCurrentGuess(([guess,cursor]) => [(guess.slice(0,cursor) + " " + guess.slice(cursor+1)).slice(0,puzzle.target.length),cursor]);
    } else if (key === "Enter") {
      let cg = currentGuess[0].trim();
      if (cg.length < minWordLength || cg.lastIndexOf(" ") !== -1) {
        setHint("Must include a word from " + minWordLength + " to " + maxWordLength + " letters");
        return;
      }
      if(guesses.includes(currentGuess[0])) {
        setHint("You've already guessed that");
        return;
      }
      if (!full_dictionary.includes(cg)) {
        setHint(`Your guess must contain some valid word.`);
        return;
      }
      setGuesses((guesses) => guesses.concat([currentGuess[0]]));
      setCurrentGuess(["       ",0]);
      notePlayed(gameName.toLowerCase(), cheat || isDev || research !== null);
      speak(describeClue(clue(currentGuess[0], puzzle.target)))
      doWinOrLose();
    }
  };

  const applyJuice = (rowIndex: number, className: string, tableClassName?: string) => {
    const tbody = tableRef.current?.querySelector("tbody");
    if (!tbody) return;
    const row = tbody.children[rowIndex] as HTMLElement | undefined;
    if (!row) return;
    className.split(" ").forEach(c => row.classList.add(c));
    const letters = row.querySelectorAll(".Row-letter");
    const target = letters.length > 0 ? letters[letters.length - 1] : row;
    target.addEventListener("animationend", () => {
      className.split(" ").forEach(c => row.classList.remove(c));
    }, { once: true });
    if (tableClassName && tableRef.current) {
      tableRef.current.classList.add(tableClassName);
      tableRef.current.addEventListener("animationend", () => {
        tableRef.current?.classList.remove(tableClassName);
      }, { once: true });
    }
  };

  const burstFromRow = (rowIndex: number, particlesPerLetter: number) => {
    const tbody = tableRef.current?.querySelector("tbody");
    if (!tbody) return;
    const row = tbody.children[rowIndex] as HTMLElement | undefined;
    if (!row) return;
    const letters = row.querySelectorAll(".Row-letter");
    const count = letters.length;
    letters.forEach((el, i) => {
      const rect = el.getBoundingClientRect();
      const x = (rect.left + rect.width / 2) / window.innerWidth;
      const y = (rect.bottom + 8) / window.innerHeight;
      const angle = 90 - (i - (count - 1) / 2) * 8;
      confetti({ particleCount: particlesPerLetter, spread: 30, angle, origin: { x, y }, gravity: 1.2, startVelocity: 15 });
    });
  };

  const doWinOrLose = () => {
    if ( gameState == GameState.Playing ) {
      if ( (guesses.includes(puzzle.target)) ) {
        setGameState(GameState.Won);
        if (juicedAtGuessCount.current !== guesses.length) {
          juicedAtGuessCount.current = guesses.length;
          const winRow = guesses.length - 1;
          const isEpic = guesses.length <= 2;
          if (isEpic) {
            applyJuice(winRow, "Row-juice-win-epic", "Game-rows-juice-win-epic");
            burstFromRow(winRow, 18);
            setTimeout(() => burstFromRow(winRow, 18), 400);
          } else {
            applyJuice(winRow, "Row-juice-win", "Game-rows-juice-win");
            burstFromRow(winRow, 15);
          }
          setKeyboardJuice(true);
        }
      } else if (guesses.length >= props.maxGuesses) {
        setGameState(GameState.Lost);
      }
    }
    setHint(getHintFromState());
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) {
        onKey(e.key);
      }
      if (!e.altKey && ["Backspace", "Delete", "SpaceBar", "ArrowLeft", "ArrowRight", " "].lastIndexOf(e.key) !== -1 && gameState == GameState.Playing) {
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [currentGuess, gameState]);

  useEffect(() => {
    doWinOrLose();
  }, [currentGuess, gameState, guesses, puzzle.target]);

  const realMaxGuesses = gameState == GameState.Won 
    ? guesses.length 
    : Math.max(guesses.length, props.maxGuesses);

  let letterInfo = new Map<string, [Clue, number]>();

  const cluedRows = Array(realMaxGuesses)
    .fill(undefined)
    .map((_,i)=>{
      const guess = [...guesses, currentGuess[0]][i] ?? "";
      return clue(guess, puzzle.target); 
    });

  const tableRows = Array(realMaxGuesses)
    .fill(undefined)
    .map((_, i) => {      
      const cluedLetters = cluedRows[i];
      const lockedIn = i < guesses.length;
      const annotation = '\u00a0';
      if (lockedIn) {
        for (const { clue, letter } of cluedLetters) {
          if (clue === undefined) break;
          const count = cluedLetters
            .filter((cl) => cl.letter === letter && cl.clue && cl.clue > Clue.Absent)
            .length;
          const old = letterInfo.get(letter);
          const newCount = old === undefined || old[1] < count ? count : old[1];
          const newClue = old === undefined || old[0] < clue ? clue : old[0]; 
          letterInfo.set(letter, [newClue, newCount]);
        }
      }
      return (
        <Row
          onClick={(rowNumber, column) => {
            setCurrentGuess([currentGuess[0],column])
          }}
          currentGuess={currentGuess}
          key={i}         
          rowState={
            lockedIn
              ? RowState.LockedIn
              : (i === guesses.length)
              ? RowState.Editing
              : RowState.Pending
          }
          cluedRows={cluedRows.slice(undefined, guesses.length)}
          cluedLetters={cluedLetters}
          letterInfo={letterInfo}
          rowNumber={i}
          annotation={annotation}
          rowWidth={puzzle.target.length}
        />
      );
    });
    
  const dateText = `, ${new Date(day1Date.getTime()+(dayNum-1)*86400*1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  const cheatText = cheat ? ` ${puzzle.target}` : "";
  const canPrev = dayNum > 1;
  const canNext = dayNum < todayDayNum || isDev;
  const practiceLink = "?unlimited";
  const prevLink = "?x=" + (dayNum-1).toString() + (isDev ? "&xyzzyx="+cheatyface["password"] : "") + (cheat ? "&cheat=1" : "") + (urlParam("preventRedirect") !== null ? "&preventRedirect" : "");
  const nextLink = "?x=" + (dayNum+1).toString() + (isDev ? "&xyzzyx="+cheatyface["password"] : "") + (cheat ? "&cheat=1" : "") + (urlParam("preventRedirect") !== null ? "&preventRedirect" : "");

  const takeoverSiteId = gameName.toLowerCase();
  const shouldCheckTakeover =
    !watching &&
    !practice &&
    dayNum === todayDayNum &&
    gameState !== GameState.Playing;

  const [takeover, setTakeover] = useState<Takeover | null>(null);
  useEffect(() => {
    if (!shouldCheckTakeover) return;
    let canceled = false;
    (async () => {
      const t = await maybeFetchTakeover(takeoverSiteId);
      if (canceled) return;
      setTakeover(t);
    })();
    return () => {
      canceled = true;
    };
  }, [shouldCheckTakeover, takeoverSiteId]);

  // Any deliberate action means they are done with it, on this device, for good.
  const dismissTakeover = (takeoverId: string) => {
    try {
      window.localStorage.setItem(getTakeoverDismissKey(takeoverSiteId, takeoverId), "1");
    } catch (e) {
      // ignore storage failures
    }
  };

  const hardModeIndicator = hardModeState ? " !!!" : "";
  
  const dayNav = (
    <div className="Game-options">
      {!practice && canPrev && <span><a className="NextPrev" href={prevLink}>«</a> </span>}
      {!practice && !canPrev && <span> <a className="NextPrev">&nbsp;</a></span>}
      {!practice && <span className="DayNum">Day {dayNum}{`${cheatText}`}{`${dateText}`}</span>}
      {!practice && canNext && <span> <a className="NextPrev" href={nextLink}>»</a></span>}
      {!practice && !canNext && <span> <a className="NextPrev">&nbsp;</a></span>}
      {canReset && <span>| <a href={window.location.href} onClick={ ()=>{resetDay();} }>Reset</a></span>}
      {practice && <span>{`${cheatText}`}</span>}
      {practice && <span>
        <a href=""
          className="practiceLink"
          onClick={(e) => {
            share(
              "Challenge link copied to clipboard!",
              ``
            );
            e.preventDefault();
          }}
        >
          Share
        </a>
        <a href={practiceLink} className="practiceLink" onClick={ ()=>{resetPractice();} }> +New </a>
        </span>
      }
    </div>
  );

  const watchButtons = gameState !== GameState.Playing && (
    <div className="button-stack">
      {liveCount > 0 && (
        <button className="watch-button" onClick={() => handleWatchClick("live")}>
          <span style={{ fontSize: "18px" }}>{"\u25B6"}</span> Watch Live ({liveCount})
        </button>
      )}
      {replayCount > 0 && (
        <button className="watch-button watch-button--replay" onClick={() => handleWatchClick("replay")}>
          <span style={{ fontSize: "18px" }}>{"\u25B6"}</span> Watch Replay ({replayCount})
        </button>
      )}
    </div>
  );

  const watchNavButtons = watching && (
    <div className="button-stack">
      {watchingKind === "live" && liveCount > 1 && (
        <button className="watch-button" onClick={() => switchToKind("live")}>
          <span style={{ fontSize: "18px" }}>{"\u25B6"}</span> Next Live ({liveCount})
        </button>
      )}
      {watchingKind === "live" && replayCount > 0 && (
        <button className="watch-button watch-button--replay" onClick={() => switchToKind("replay")}>
          <span style={{ fontSize: "18px" }}>{"\u25B6"}</span> Watch Replay ({replayCount})
        </button>
      )}
      {watchingKind === "replay" && liveCount > 0 && (
        <button className="watch-button" onClick={() => switchToKind("live")}>
          <span style={{ fontSize: "18px" }}>{"\u25B6"}</span> Watch Live ({liveCount})
        </button>
      )}
      {watchingKind === "replay" && replayCount > 1 && (
        <button className="watch-button watch-button--replay" onClick={() => switchToKind("replay")}>
          <span style={{ fontSize: "18px" }}>{"\u25B6"}</span> Next Replay ({replayCount})
        </button>
      )}
      <button className="copy-result-button" onClick={() => { setWatching(false); }}>
        Your Solution
      </button>
    </div>
  );

  return (
    <div className="Game" style={{ display: props.hidden ? "none" : "block" }}>
      {!watching && showWatchIntro && (
        <PopUp
          title="Watch Mode"
          element={
            <div>
              <p>You can watch other players's solutions live or as a replay after solving a puzzle yourself, and likewise your solution goes into the pool for others to watch live or as a replay.</p>
              <p>You can change this anytime in Settings.</p>
              <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: "18px" }}>
                <button className="watch-button" onClick={() => {
                  window.localStorage.setItem("shuntle-watch-intro-seen", "1");
                  props.setLiveReplays(true);
                  setShowWatchIntro(false);
                  if (liveCount > 0) {
                    startWatchingKind("live");
                  } else if (replayCount > 0) {
                    startWatchingKind("replay");
                  }
                }}>
                  <span style={{ fontSize: "18px" }}>{"\u25B6"}</span> Enable and watch
                </button>
                <button className="modal-dismiss-button" onClick={() => setShowWatchIntro(false)}>
                  Dismiss
                </button>
              </div>
            </div>
          }
          onClick={() => setShowWatchIntro(false)}
        />
      )}
      {dayNav}
      {watching && (
        <WatchView
          puzzleId={puzzleId}
          colorBlind={props.colorBlind}
          keyboardLayout={props.keyboardLayout}
          targetUserId={watchingUserId}
        >
          {watchNavButtons}
        </WatchView>
      )}
      {!watching && (<>
      <table
        className="Game-rows"
        tabIndex={0}
        aria-label="table of guesses"
        ref={tableRef}
      >
        <tbody>{tableRows}</tbody>
      </table>
      <div
        role="alert"
        style={{
          userSelect: /https?:/.test(hint) ? "text" : "none",
          whiteSpace: "pre-wrap",
        }}
      >
        {(hint || `\u00a0`)}
        {gameState !== GameState.Playing && (
          <p>
          <button
            className="copy-result-button"
            onClick={() => {
              const score = gameState === GameState.Lost ? "X" : guesses.length.toString();
              share(
                "Result copied to clipboard!",
                `${gameName} ${practice ? ("unlimited " + currentSeed.toString()) : ("#"+dayNum.toString())}${hardModeIndicator} ${score}/${props.maxGuesses}\n` +
                emojiBlock({guesses:guesses, puzzle:puzzle, gameState:gameState, hardMode:hardModeState}, props.colorBlind)
              );
            }}
          >
            Share
          </button>
          </p>
        )}
      </div>
      {watchButtons}
      {shouldCheckTakeover && takeover && (
        <TakeoverNotice
          takeover={takeover}
          onDismiss={() => dismissTakeover(takeover.id)}
        />
      )}
      {readOnly() && (
        <p>
        <button onClick={() => {copyImportCode();}} >
          Copy import code
        </button>
        </p>
      )}
      {!readOnly() && (
        <Keyboard
          layout={props.keyboardLayout}
          letterInfo={letterInfo}
          onKey={onKey}
          juiceGreen={keyboardJuice}
          onJuiceDone={() => setKeyboardJuice(false)}
        />)}
      </>)}
    </div>
  );
}

export default Game;
